import { parseCalendarDate } from '@hkdevs/design-system';
import { defaultParseSearch, defaultStringifySearch } from '@tanstack/react-router';

/**
 * Dates in URLs are MM-DD-YYYY (/pay/checks/10-09-2026, ?from=06-24-2026); inside the
 * app and in API calls they are ISO. These convert at the URL boundary: the check routes'
 * params and the router's search parsing, so views and their search parsers only see ISO.
 */

const URL_DATE = /^(\d{2})-(\d{2})-(\d{4})$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** "10-09-2026" as "2026-10-09", or null if it isn't a real day in that form. */
export function isoFromUrlDate(text: string): string | null {
  const match = URL_DATE.exec(text);
  if (!match) return null;
  const [, month, day, year] = match;
  const iso = `${year}-${month}-${day}`;
  return parseCalendarDate(iso) ? iso : null;
}

/** "2026-10-09" as "10-09-2026". Anything else is returned as it is. */
export function urlDateFromIso(iso: string): string {
  const match = ISO_DATE.exec(iso);
  if (!match) return iso;
  const [, year, month, day] = match;
  return `${month}-${day}-${year}`;
}

/** The search params that hold dates, on every route that has them. */
const DATE_KEYS = ['from', 'to'] as const;

/** The router's parseSearch: date params become ISO; any other form is dropped. */
export function parseSearch(searchStr: string): Record<string, unknown> {
  const search: Record<string, unknown> = defaultParseSearch(searchStr);
  for (const key of DATE_KEYS) {
    if (!(key in search)) continue;
    const value = search[key];
    const iso = typeof value === 'string' ? isoFromUrlDate(value) : null;
    if (iso) search[key] = iso;
    else delete search[key];
  }
  return search;
}

/** The router's stringifySearch: ISO date params are written as MM-DD-YYYY. */
export function stringifySearch(search: Record<string, unknown>): string {
  const out = { ...search };
  for (const key of DATE_KEYS) {
    const value = out[key];
    if (typeof value === 'string') out[key] = urlDateFromIso(value);
  }
  return defaultStringifySearch(out);
}

/**
 * A check route's pay date from its path: ISO, or '' for anything that isn't a real
 * MM-DD-YYYY day (an ISO date included), so the route shows not found.
 */
export const payDateFromPath = (text: string) => isoFromUrlDate(text) ?? '';
